import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { previewBokunRefund, type RefundPreview } from "@/lib/bokun-refunds.functions";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Cancelación y reembolso — MODO SIMULADO.
 * Solo calcula la vista previa (política de Bókun, importe, relación con Stripe).
 * No cancela en Bókun ni reembolsa en Stripe: el botón de ejecución está
 * deshabilitado hasta la autorización final del titular.
 */
export const Route = createFileRoute("/_authenticated/admin/reembolsos")({
  head: () => ({ meta: [{ title: "Cancelación y reembolso (simulado) — Admin" }, { name: "robots", content: "noindex" }] }),
  component: RefundsPage,
});

function fmt(minor?: number, cur?: string) {
  if (minor == null) return "—";
  return `${(minor / 100).toFixed(2)} ${cur ?? ""}`;
}

function RefundsPage() {
  const [code, setCode] = useState("");
  const m = useMutation({
    mutationFn: (c: string) => previewBokunRefund({ data: { code: c } }) as Promise<RefundPreview>,
  });
  const p = m.data;

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
      <AdminNav />
      <h1 className="text-2xl font-semibold">Cancelación y reembolso</h1>
      <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
        Modo simulado: se calcula lo que ocurriría, pero no se cancela en Bókun ni se devuelve dinero en Stripe.
        Requiere rol Propietario o Finanzas con verificación en dos pasos.
      </p>
      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) m.mutate(code.trim().toUpperCase());
        }}
      >
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="refund-code">Código de reserva</Label>
          <Input id="refund-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="MOS-000000" />
        </div>
        <Button type="submit" disabled={m.isPending}>{m.isPending ? "Calculando…" : "Simular"}</Button>
      </form>

      {m.error && <p className="text-sm text-destructive">{(m.error as Error).message}</p>}
      {p && !p.ok && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{p.error}</p>}
      {p?.ok && (
        <div className="space-y-2 rounded-2xl border bg-card p-5 text-sm">
          <Row k="Entorno" v={p.environment === "live" ? "Producción" : "Prueba"} />
          <Row k="Tour" v={p.tourTitle ?? "—"} />
          <Row k="Fecha" v={p.travelDate ?? "—"} />
          <Row k="Pagado" v={fmt(p.paidMinor, p.currency)} />
          <Row k="Horas antes del tour" v={p.hoursBeforeTour == null ? "—" : String(Math.round(p.hoursBeforeTour))} />
          <Row k="Política (Bókun)" v={p.policyTitle ?? "Sin política estructurada"} />
          <Row k="Tipo de reembolso" v={p.refundType === "full" ? "Total" : p.refundType === "partial" ? "Parcial" : "Sin reembolso"} />
          <Row k="Importe a devolver" v={fmt(p.refundMinor, p.currency)} />
          <Row k="Payment Intent" v={p.paymentIntentId ?? "—"} />
          <Row k="Cargo" v={p.chargeId ?? "—"} />
          {p.existing && <Row k="Operación previa" v={`${p.existing.state}${p.existing.lastError ? ` · ${p.existing.lastError}` : ""}`} />}
          <Button className="mt-3 w-full" disabled title="Deshabilitado en modo simulado">
            Ejecutar cancelación y reembolso (deshabilitado)
          </Button>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4 border-b py-1 last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="break-all text-right font-medium">{v}</span>
    </div>
  );
}
